import {
  Box,
  Heading,
  Text,
  Badge,
  HStack,
  VStack,
  useColorModeValue,
  IconButton,
  Icon,
  Flex,
  Spacer,
  Button,
  Avatar,
} from '@chakra-ui/react';
import {
  EmailIcon,
  PhoneIcon,
  AddIcon,
  WarningIcon,
  EditIcon,
} from '@chakra-ui/icons';

import { useToast } from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { deleteEmployee } from '../../hooks/employees';
import { FiMail, FiPhone } from 'react-icons/fi';
import { Employee } from '../../utils/types';
import { ElementType } from 'react';
import DeleteTeamMemberButton from '../../pages/team/DeleteTeamMemberButton';

const DeleteEmployeeButton = ({
  employeeId,
  orgId,
  onDeleted,
}: {
  employeeId: string;
  orgId: string;
  onDeleted?: () => void; // optional callback
}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const handleDelete = async () => {
    const confirm = window.confirm(
      'Are you sure you want to delete this employee?',
    );
    if (!confirm) return;

    try {
      let response = await deleteEmployee(orgId, employeeId);
      console.log('delete response', response);
      if (response?.success) {
        toast({
          title: 'Employee deleted.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        window.location.reload();

        if (onDeleted) {
          onDeleted(); // trigger callback (e.g. reload list)
        } else {
          // navigate(`/organization/${orgId}/employees`); // default redirect
        }
      }
    } catch (error) {
      console.error('Failed to delete employee:', error);
      toast({
        title: 'Error deleting employee.',
        description: 'Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Button
      leftIcon={<DeleteIcon />}
      colorScheme="red"
      variant="outline"
      onClick={handleDelete}
    >
      {/* Delete Employee */}
    </Button>
  );
};

export default DeleteEmployeeButton;

export function EmployeeCard({
  employee,
  meEmployee,
  employeesCount,
  orgId,
  showAddButton = false,
  onAddClick,
}: {
  employee: Employee;
  meEmployee?: Employee;
  employeesCount: number;
  orgId: string;
  showAddButton?: boolean;
  onAddClick?: () => void;
}) {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('md', 'dark-lg');

  return (
    <Box
      key={employee.id}
      p={5}
      bg={cardBg || 'white'}
      shadow="md"
      rounded="2xl"
      border="1px solid"
      borderColor={employee.user ? 'green.100' : 'gray.100'}
      cursor="pointer"
      transition="all 0.3s ease-in-out"
      _hover={{
        transform: 'scale(1.02)',
        shadow: 'xl',
        borderColor: 'green.400',
      }}
      onClick={() => navigate(`/organization/${orgId}/team/${employee.id}`)}
    >
      <HStack spacing={4} align="start" flexWrap="wrap">
        {/* Avatar */}
        <Avatar
          name={employee?.user?.first_name || 'Unknown'}
          size="lg"
          bg={employee?.user ? 'green.100' : 'gray.200'}
          color="green.700"
          shadow="sm"
        />

        {/* Employee Info */}
        <VStack align="start" spacing={2} flex={1}>
          <Flex w="100%" align="center">
            <Heading size="md" color="green.700">
              {`${employee?.user?.first_name} ${employee?.user?.last_name}` ||
                'Unnamed Employee'}
            </Heading>
            <Spacer />
            <Badge
              colorScheme="blue"
              fontSize="xs"
              px={2}
              py={1}
              borderRadius="md"
            >
              {employee.role || 'Not specified'}
            </Badge>
          </Flex>

          {/* Contact Info */}
          <VStack align="start" spacing={1}>
            {employee.user?.email && (
              <HStack spacing={2} color="gray.600">
                <Icon as={FiMail as ElementType} boxSize={4} />
                <Text fontSize="sm">{employee.user.email}</Text>
              </HStack>
            )}

            {!employee?.user && (
              <HStack spacing={2} color="gray.500">
                <WarningIcon />
                <Text fontSize="sm">
                  No associated user. Consider deleting this employee.
                </Text>
              </HStack>
            )}
          </VStack>
        </VStack>

        {/* Action Buttons */}
        <VStack spacing={2} align="end">
          <IconButton
            aria-label="Edit Employee"
            icon={<EditIcon />}
            size="sm"
            colorScheme="yellow"
            variant="ghost"
            _hover={{ bg: 'yellow.100' }}
            onClick={(e) => {
              e.stopPropagation(); // prevent card click
              navigate(`/organization/${orgId}/team/${employee.id}/edit`);
            }}
          />

          <DeleteTeamMemberButton
            orgId={orgId}
            employee={employee}
            employeesCount={employeesCount}
            currentUserRole={meEmployee?.role || 'Member'}
            onClick={(e: any) => {
              return e.stopPropagation();
            }} // prevent card click
          />
        </VStack>
      </HStack>
    </Box>
  );
}
